import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { useTranslation } from 'react-i18next';


const About = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-24 md:py-32 pt-[120px]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">About Daily Express Cargo</h1>
          <p className="text-xl md:text-2xl text-blue-100">Delivering Excellence in Logistics & Transportation</p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-20">
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-widest mb-4">Our Story</p>
            <div className="w-24 h-[2px] bg-blue-600 mx-auto mb-8"></div>
            <h2 className="text-5xl font-bold text-gray-900 leading-tight">
              Built on Trust, Technology,<br className="hidden md:block"/> and Excellence
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-12">
            <div className="bg-white rounded-lg shadow-md p-8 border-l-4 border-blue-600">
              <p className="text-xl text-gray-800 leading-relaxed font-light">
                <span className="font-semibold">Daily Express Cargo</span> is a trusted logistics partner committed to delivering reliable, 
                efficient, and transparent cargo solutions with operational excellence.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                  To deliver world-class logistics solutions that empower businesses to grow by providing fast, reliable, and transparent shipment services.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                  To become India's most trusted logistics provider by combining cutting-edge technology with dedicated customer service.
                </p>
              </div>
            </div>

            <p className="text-lg text-gray-600 leading-relaxed font-light bg-blue-50 p-8 rounded-lg">
              With years of logistics experience and operational excellence, we deliver transport services tailored to real business needs. 
              Our advanced systems and skilled team ensure accuracy, real-time tracking, and consistent service quality across every shipment.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-widest mb-4">Values</p>
            <div className="w-24 h-[2px] bg-blue-600 mx-auto mb-8"></div>
            <h2 className="text-4xl font-bold text-gray-900">What We Stand For</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-blue-600 text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Integrity</h3>
              <p className="text-gray-600 text-sm">Honest and transparent in all dealings</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-green-600 text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Reliability</h3>
              <p className="text-gray-600 text-sm">Consistent delivery and service excellence</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-purple-600 text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Innovation</h3>
              <p className="text-gray-600 text-sm">Leveraging technology for better solutions</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-orange-600 text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Excellence</h3>
              <p className="text-gray-600 text-sm">Commitment to quality in every aspect</p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-24 bg-blue-600 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">By The Numbers</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">500+</div>
              <p className="text-blue-100">Active Customers</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">50K+</div>
              <p className="text-blue-100">Monthly Shipments</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">98%</div>
              <p className="text-blue-100">On-Time Delivery</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">5+</div>
              <p className="text-blue-100">Years of Service</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
